/* ============================================================================
 * Lane 2B — shared, pure domain-scoping helpers for the SEO module pages.
 *
 * The SEO pages read the ACTIVE domain from the shared `useActiveDomain()` hook
 * (Lane 2A) and pass its project id to their existing per-project fetchers. This
 * module holds the pure, side-effect-free derivation logic so it can be unit
 * tested under `node --test` (no DOM), independently of React.
 *
 * Invariants enforced here (multi-domain safety — multi-domain-audit-2026-07-19):
 *  - NEVER default to the first option (`options[0]` / ORDER BY id ASC trap):
 *    when no domain is active, a page must REQUIRE a choice, not silently pick
 *    booksesh.com.
 *  - Only honour an active domain that is actually in the loaded workspace option
 *    list (guards a stale/foreign active id — the tenancy authority remains the
 *    server; the active id is a view preference only).
 *  - Row filtering by domain is exact on `project_id` — no cross-domain mixing.
 * ==========================================================================*/

/** Minimal structural view of the shared active domain (from useActiveDomain). */
export interface ActiveDomainLike {
  projectId: number;
}

/**
 * Which project (domain) id a domain-scoped page should read.
 * Returns null when nothing is legitimately active — the page then requires a
 * domain choice and fetches nothing (no first-domain fallback).
 */
export function resolveActiveProjectId(
  active: ActiveDomainLike | null,
  options: ReadonlyArray<{ id: string }>,
): string | null {
  if (!active) return null;
  const id = String(active.projectId);
  return options.some((o) => o.id === id) ? id : null;
}

/**
 * Filter domain-attributed rows (each carrying a nullable `project_id`) to the
 * active domain. With no active domain the full set is returned unchanged so a
 * page can show an honest, per-row-attributed "all domains" breakdown — never a
 * merged single blob (aggregation rule §2.4).
 */
export function filterRowsByActiveDomain<T extends { project_id: number | null }>(
  rows: ReadonlyArray<T>,
  active: ActiveDomainLike | null,
): T[] {
  if (!active) return rows.slice();
  return rows.filter((r) => r.project_id === active.projectId);
}

/** Human label for the current SEO scope, for honest attribution in the UI. */
export function scopeLabel(active: { hostname?: string } | null): string {
  return active && active.hostname ? `Viewing ${active.hostname}` : 'Across your workspace — all domains';
}
